<template>
  <div class="seminar-page">
    <!-- HERO -->
    <div class="seminar-hero">
      <div class="seminar-hero-bg">
        <img src="img/prod.jpeg" :alt="$t('seminar.hero.titleGreen')" />
        <div class="seminar-hero-fade-left"></div>
        <div class="seminar-hero-fade-right"></div>
        <div class="seminar-hero-navbar-shade"></div>
      </div>
      <div class="container seminar-hero-content">
        <div class="row align-items-center">
          <div class="col-lg-6">
            <div class="seminar-eyebrow seminar-eyebrow-hero">&mdash; {{ $t('seminar.hero.eyebrow') }}</div>
            <h1 class="seminar-hero-title">
              <span class="text-green">{{ $t('seminar.hero.titleGreen') }}</span><br>
              <span class="text-dark">{{ $t('seminar.hero.titleBlack') }}</span>
            </h1>
            <h5 class="seminar-hero-subtitle">
              {{ $t('seminar.hero.subtitleLine1') }}<br>{{ $t('seminar.hero.subtitleLine2') }}
            </h5>
            <p class="seminar-hero-desc">{{ $t('seminar.hero.description') }}</p>
            <div class="seminar-hero-actions">
              <n-button round class="font-weight-bold mr-3 seminar-btn-gold" @click="scrollToForm">
                {{ $t('seminar.hero.registerBtn') }} <i class="fa fa-arrow-right ml-1"></i>
              </n-button>
              <n-button round outline class="font-weight-bold seminar-btn-outline" @click="scrollToAbout">
                {{ $t('seminar.hero.learnMoreBtn') }}
              </n-button>
            </div>
          </div>
          <div class="col-lg-6 d-none d-lg-block">
            <div class="seminar-hero-badge">
              <div class="seminar-hero-badge-small">{{ $t('seminar.hero.badgeSmall') }}</div>
              <div class="seminar-hero-badge-line">{{ $t('seminar.hero.badgeTagline1') }}</div>
              <div class="seminar-hero-badge-line">{{ $t('seminar.hero.badgeTagline2') }}</div>
              <div class="seminar-hero-badge-line">{{ $t('seminar.hero.badgeTagline3') }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ABOUT + TOPICS + FORM -->
    <div class="section seminar-section-alt" ref="about">
      <div class="container">
        <div class="seminar-eyebrow seminar-eyebrow-hero">&mdash; {{ $t('seminar.about.eyebrow') }}</div>
        <div class="row">
          <div class="col-lg-6">
            <h2 class="seminar-section-heading">{{ $t('seminar.about.heading') }}</h2>
            <span class="seminar-heading-bar"></span>
            <p class="seminar-note">{{ $t('seminar.about.intro') }}</p>

            <div class="row mt-4">
              <div class="col-md-6 mb-4" v-for="n in 6" :key="n">
                <div class="seminar-topic-item">
                  <div class="seminar-topic-icon">
                    <i :class="'fa ' + topicIcons[n-1]"></i>
                  </div>
                  <div>
                    <h6 class="font-weight-bold mb-1">{{ $t('seminar.about.topics.t' + n + '.title') }}</h6>
                    <p class="mb-0" v-if="$t('seminar.about.topics.t' + n + '.desc')">{{ $t('seminar.about.topics.t' + n + '.desc') }}</p>
                  </div>
                </div>
              </div>
            </div>

            <p class="seminar-note mt-2">{{ $t('seminar.about.customizationNote') }}</p>
          </div>

          <div class="col-lg-6" ref="formSection">
            <div class="seminar-form-card">
              <h4 class="font-weight-bold mb-1"><span class="seminar-form-heading-bar"></span>{{ $t('seminar.form.heading') }}</h4>
              <p class="text-muted mb-4">{{ $t('seminar.form.subheading') }}</p>

              <label class="mt-2 font-weight-bold">{{ $t('seminar.form.fullName') }} <span class="text-danger">*</span></label>
              <fg-input class="has-success input-lg mb-1" :placeholder="$t('seminar.form.fullNamePlaceholder')" required v-model="requestData.fullName"></fg-input>

              <label class="mt-2 font-weight-bold">{{ $t('seminar.form.companyName') }} <span class="text-danger">*</span></label>
              <fg-input class="has-success input-lg mb-1" :placeholder="$t('seminar.form.companyNamePlaceholder')" required v-model="requestData.companyName"></fg-input>

              <label class="mt-2 font-weight-bold">{{ $t('seminar.form.jobTitle') }} <span class="text-danger">*</span></label>
              <fg-input class="has-success input-lg mb-1" :placeholder="$t('seminar.form.jobTitlePlaceholder')" required v-model="requestData.jobTitle"></fg-input>

              <label class="mt-2 font-weight-bold">{{ $t('seminar.form.email') }} <span class="text-danger">*</span></label>
              <fg-input class="has-success input-lg mb-1" :placeholder="$t('seminar.form.emailPlaceholder')" required type="email" v-model="requestData.email"></fg-input>

              <label class="mt-2 font-weight-bold">{{ $t('seminar.form.phoneNumber') }} <span class="text-danger">*</span></label>
              <fg-input class="has-success input-lg mb-1" :placeholder="$t('seminar.form.phoneNumberPlaceholder')" required v-model="requestData.phoneNumber"></fg-input>

              <label class="mt-2 font-weight-bold">{{ $t('seminar.form.industry') }} <span class="text-danger">*</span></label>
              <select class="form-control has-success input-lg mb-1" v-model="requestData.industry" required>
                <option value="">{{ $t('seminar.form.industryPlaceholder') }}</option>
                <option v-for="(ind, i) in $t('seminar.form.industries')" :key="i" :value="ind">{{ ind }}</option>
              </select>

              <div class="row">
                <div class="col-md-6">
                  <label class="mt-2 font-weight-bold">{{ $t('seminar.form.preferredDate') }}</label>
                  <fg-input class="has-success input-lg mb-1" type="date" v-model="requestData.preferredDate"></fg-input>
                </div>
                <div class="col-md-6">
                  <label class="mt-2 font-weight-bold">{{ $t('seminar.form.participants') }}</label>
                  <fg-input class="has-success input-lg mb-1" :placeholder="$t('seminar.form.participantsPlaceholder')" type="number" min="1" v-model="requestData.participants"></fg-input>
                </div>
              </div>

              <label class="mt-2 font-weight-bold">{{ $t('seminar.form.location') }}</label>
              <select class="form-control has-success input-lg mb-1" v-model="requestData.locationPreference">
                <option value="">{{ $t('seminar.form.locationPlaceholder') }}</option>
                <option value="client_site">{{ $t('seminar.form.locationClientSite') }}</option>
                <option value="tef_office">{{ $t('seminar.form.locationTefOffice') }}</option>
              </select>

              <label class="mt-2 font-weight-bold">{{ $t('seminar.form.message') }}</label>
              <textarea
                style="border-radius: 10px; border: 1px solid #dcdcdc; color: #808080; font-size: 12px; width: 100%;"
                rows="4"
                :placeholder="$t('seminar.form.messagePlaceholder')"
                v-model="requestData.message"
              ></textarea>

              <n-button
                size="lg"
                class="font-weight-bold w-100 mt-4 seminar-btn-gold seminar-btn-submit"
                @click="save()"
                :disabled="onLoading"
              >
                <span v-if="onLoading"><i class="fa fa-spinner fa-spin"></i> {{ $t('seminar.form.pleaseWait') }}</span>
                <span v-else>{{ $t('seminar.form.submit') }} <i class="fa fa-arrow-right ml-1" aria-hidden="true"></i></span>
              </n-button>
              <p class="mt-3 mb-0 text-center"><small class="text-muted"><i class="fa fa-lock mr-1"></i>{{ $t('seminar.form.confidentialNotice') }}</small></p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- SEMINAR LOCATIONS -->
    <div class="section">
      <div class="container">
        <h2 class="seminar-section-heading seminar-dash-heading"><span class="seminar-heading-dash"></span>{{ $t('seminar.locations.heading') }}</h2>
        <p class="seminar-note mb-4">{{ $t('seminar.locations.description') }}</p>
        <div class="row">
          <div class="col-lg-6 mb-4">
            <div class="seminar-location-card">
              <div class="seminar-location-info">
                <div class="seminar-location-icon"><i class="fa fa-industry"></i></div>
                <div class="seminar-location-title">{{ $t('seminar.locations.clientSite.title') }}</div>
                <div class="seminar-location-arrow"><i class="fa fa-arrow-right"></i></div>
              </div>
              <div class="seminar-location-img"><img src="img/facility/crane.jpg" alt="" /></div>
            </div>
          </div>
          <div class="col-lg-6 mb-4">
            <div class="seminar-location-card">
              <div class="seminar-location-info">
                <div class="seminar-location-icon"><i class="fa fa-building"></i></div>
                <div class="seminar-location-title">{{ $t('seminar.locations.tefOffice.title') }}</div>
                <div class="seminar-location-arrow"><i class="fa fa-arrow-right"></i></div>
              </div>
              <div class="seminar-location-img"><img src="img/facility/DSC_3387.jpg" alt="" /></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- WHO SHOULD ATTEND -->
    <div class="section">
      <div class="container">
        <h2 class="seminar-section-heading seminar-dash-heading mb-4"><span class="seminar-heading-dash"></span>{{ $t('seminar.audience.heading') }}</h2>
        <div class="row">
          <div class="col-6 col-md-3 col-lg mb-4 text-center" v-for="(role, i) in $t('seminar.audience.roles')" :key="i">
            <div class="seminar-audience-icon"><i :class="'fa ' + audienceIcons[i]"></i></div>
            <p class="font-weight-bold mb-0 seminar-audience-label">{{ role }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- PROCESS FLOW -->
    <div class="seminar-process-section">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-5 mb-4 mb-lg-0">
            <h2 class="seminar-section-heading text-white mb-2">
              {{ $t('seminar.process.headingLine1') }}<br>{{ $t('seminar.process.headingLine2') }}
            </h2>
            <span class="seminar-heading-bar seminar-heading-bar-gold"></span>
            <p class="seminar-process-desc">{{ $t('seminar.process.description') }}</p>
          </div>
          <div class="col-lg-7">
            <div class="seminar-process-flow">
              <div class="seminar-process-item" v-for="(step, i) in $t('seminar.process.steps')" :key="i">
                <div class="seminar-process-icon"><i :class="'fa ' + processIcons[i]"></i></div>
                <p class="text-white font-weight-bold mb-0">{{ step }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- OUR COMMITMENT + HAVE QUESTIONS -->
    <div class="section">
      <div class="container">
        <div class="seminar-commitment-card">
          <div class="row align-items-center">
            <div class="col-lg-4 mb-4 mb-lg-0">
              <h2 class="seminar-section-heading seminar-dash-heading mb-2"><span class="seminar-heading-dash"></span>{{ $t('seminar.commitment.heading') }}</h2>
              <p class="seminar-note mb-0">{{ $t('seminar.commitment.description') }}</p>
            </div>
            <div class="col-lg-4 mb-4 mb-lg-0">
              <div class="seminar-doctor-card">
                <div class="seminar-doctor-brand">TOKKI (TEF)</div>
                <span class="seminar-heading-bar seminar-heading-bar-gold seminar-doctor-bar"></span>
                <div class="seminar-doctor-tagline">{{ $t('seminar.hero.badgeSmall') }}</div>
              </div>
            </div>
            <div class="col-lg-4">
              <div class="seminar-indonesia-tagline" v-html="$t('seminar.commitment.tagline')"></div>
              <div class="seminar-indonesia-subtagline" v-html="$t('seminar.commitment.subtagline')"></div>
            </div>
          </div>

          <hr class="seminar-commitment-divider">

          <div class="row align-items-center">
            <div class="col-lg-7 seminar-contact-col">
              <h2 class="seminar-section-heading seminar-dash-heading mb-2"><span class="seminar-heading-dash seminar-heading-dash-gold"></span>{{ $t('seminar.contact.heading') }}</h2>
              <p class="seminar-note mb-3">{{ $t('seminar.contact.description') }}</p>
              <router-link to="/contact-us">
                <n-button round class="font-weight-bold seminar-btn-gold">
                  {{ $t('seminar.contact.button') }} <i class="fa fa-arrow-right ml-1"></i>
                </n-button>
              </router-link>
            </div>
            <div class="col-lg-5 seminar-contact-col seminar-contact-col-right mt-4 mt-lg-0">
              <i class="fa fa-users seminar-contact-icon"></i>
              <p class="seminar-note mb-0">{{ $t('seminar.contact.tagline') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { FormGroupInput, Button } from '@/components';
import Api from '@/helpers/api';
import seminar from '@/services/seminar.service';

export default {
  name: 'profile',
  bodyClass: 'profile-page',
  components: {
    [FormGroupInput.name]: FormGroupInput,
    [Button.name]: Button,
  },
  data() {
    return {
      onLoading: false,
      requestData: {},
      topicIcons: ['fa-cog', 'fa-bullseye', 'fa-tint', 'fa-clipboard', 'fa-shield-alt', 'fa-search'],
      audienceIcons: ['fa-user', 'fa-wrench', 'fa-cogs', 'fa-search', 'fa-industry', 'fa-clipboard', 'fa-graduation-cap'],
      processIcons: ['fa-file-alt', 'fa-share-alt', 'fa-wrench', 'fa-bolt', 'fa-search', 'fa-cog', 'fa-wrench'],
    };
  },
  methods: {
    scrollToForm() {
      const el = this.$refs.formSection;
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    scrollToAbout() {
      const el = this.$refs.about;
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    save() {
      let context = this;
      this.onLoading = true;

      if (
        this.requestData.fullName == undefined ||
        this.requestData.companyName == undefined ||
        this.requestData.email == undefined ||
        this.requestData.phoneNumber == undefined
      ) {
        context.onLoading = false;
        return alert(context.$t('seminar.form.requiredNotice'));
      }

      const payload = {
        full_name: this.requestData.fullName,
        company_name: this.requestData.companyName,
        industry: this.requestData.industry || '',
        job_title: this.requestData.jobTitle || '',
        email: this.requestData.email,
        phone_number: this.requestData.phoneNumber,
        preferred_date: this.requestData.preferredDate || '',
        location_preference: this.requestData.locationPreference || '',
        participants: this.requestData.participants || '',
        message: this.requestData.message || '',
      };

      Api(context, seminar.register(payload))
        .onSuccess(function () {
          context.$notify(context.$t('seminar.form.successMessage'), 'success');
          context.requestData = {};
        })
        .onError(function (error) {
          let errorMsg = context.$t('seminar.form.errorMessage');
          const data = error.response && error.response.data;
          if (data) {
            if (data.errors) {
              const messages = Object.values(data.errors).flat();
              if (messages.length) {
                errorMsg = messages.join(' ');
              }
            } else if (data.message) {
              errorMsg = data.message;
            }
          }
          context.$notify(errorMsg, 'error');
        })
        .onFinish(function () {
          context.onLoading = false;
        })
        .call();
    },
  },
};
</script>
<style scoped>
.seminar-section-alt {
  background-color: #f7f9f8;
}

.seminar-eyebrow {
  color: #008c45;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 14px;
}

.seminar-eyebrow-dark {
  color: #f5a623;
}

.seminar-eyebrow-hero {
  color: #1a1a1a;
}

.seminar-section-heading {
  font-weight: 800;
  color: #1a1a1a;
  margin-bottom: 20px;
}

.seminar-form-heading-bar {
  display: inline-block;
  width: 22px;
  height: 4px;
  border-radius: 2px;
  background: linear-gradient(90deg, #008c45, #f5a623);
  margin-right: 10px;
  margin-bottom: 3px;
}

.seminar-dash-heading {
  display: flex;
  align-items: center;
}

.seminar-heading-dash {
  display: inline-block;
  width: 20px;
  height: 3px;
  border-radius: 2px;
  background: #008c45;
  margin-right: 10px;
  margin-bottom: 2px;
}

.seminar-heading-bar {
  display: block;
  width: 48px;
  height: 4px;
  border-radius: 2px;
  background: #008c45;
  margin: -14px 0 20px;
}

.seminar-note {
  color: #6c757d;
}

/* ---------- Hero ---------- */
.seminar-hero {
  position: relative;
  overflow: hidden;
  min-height: 580px;
  display: flex;
  align-items: center;
  padding: 150px 0 70px;
}

.seminar-hero-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.seminar-hero-bg img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* White fade so the dark hero text on the left stays readable directly on
   top of the background photo. */
.seminar-hero-fade-left {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.99) 0%, rgba(255, 255, 255, 0.97) 30%, rgba(255, 255, 255, 0.8) 50%, rgba(255, 255, 255, 0) 70%);
}

/* Dark fade on the right so the white badge text stays readable over the
   photo, without hiding the photo itself like a flat tint would. */
.seminar-hero-fade-right {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(0, 0, 0, 0) 55%, rgba(0, 0, 0, 0.2) 72%, rgba(0, 0, 0, 0.6) 100%);
}

/* Darkens just the strip behind the navbar on the left, where the white
   fade would otherwise wash out the menu text. Fades out to the right,
   where the fade-right gradient already gives the text enough contrast. */
.seminar-hero-navbar-shade {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 90px;
  background: linear-gradient(90deg, rgba(0, 0, 0, 0.45) 0%, rgba(0, 0, 0, 0.3) 45%, rgba(0, 0, 0, 0) 75%);
}

.seminar-hero-content {
  position: relative;
  z-index: 1;
}

.seminar-hero-title {
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 14px;
}

.seminar-hero-title .text-green {
  color: #008c45;
}

.seminar-hero-title .text-dark {
  color: #1a1a1a;
}

.seminar-hero-subtitle {
  color: #1a1a1a;
  font-weight: 700;
  margin-bottom: 16px;
}

.seminar-hero-desc {
  color: #495057;
  font-weight: 600;
  margin-bottom: 24px;
}

.seminar-btn-gold {
  background-color: #f5a623 !important;
  border-color: #f5a623 !important;
  color: #fff !important;
}

.seminar-btn-submit {
  border-radius: 10px !important;
}

.seminar-btn-gold:hover {
  background-color: #e0961a !important;
  border-color: #e0961a !important;
}

.seminar-btn-outline {
  border: 2px solid #008c45 !important;
  color: #008c45 !important;
  background: transparent !important;
}

.seminar-hero-badge {
  text-align: right;
  padding-right: 10px;
}

.seminar-hero-badge-small {
  font-family: 'Caveat', cursive;
  font-weight: 700;
  font-size: 40px;
  line-height: 1.15;
  margin-bottom: 14px;
  color: #fff;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
}

.seminar-hero-badge-line {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.8;
  color: #fff;
}

/* ---------- Topics ---------- */
.seminar-topic-item {
  height: 100%;
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.seminar-topic-icon {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  color: #008c45;
  font-size: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.seminar-topic-item h6 {
  color: #1a1a1a;
  font-size: 14px;
  text-transform: none;
}

.seminar-topic-item p {
  color: #495057;
  font-weight: 400;
  font-size: 13px;
}

/* ---------- Form ---------- */
.seminar-form-card {
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 10px 35px rgba(0, 0, 0, 0.08);
  border: 1px solid #eee;
}

.seminar-form-card h4 {
  color: #1a1a1a;
  display: flex;
  align-items: center;
}

.seminar-form-card label {
  color: #1a1a1a;
  font-size: 13px;
}

/* ---------- Locations ---------- */
.seminar-location-card {
  display: flex;
  align-items: stretch;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #eee;
  background: #fff;
  height: 170px;
}

.seminar-location-info {
  flex: 0 0 46%;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.seminar-location-icon {
  color: #008c45;
  font-size: 24px;
  margin-bottom: 8px;
}

.seminar-location-title {
  font-weight: 700;
  color: #1a1a1a;
  font-size: 15px;
  line-height: 1.35;
}

.seminar-location-arrow {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1.5px solid #1a1a1a;
  color: #1a1a1a;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 10px;
}

.seminar-location-img {
  flex: 1;
}

.seminar-location-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* ---------- Who Should Attend ---------- */
.seminar-audience-icon {
  width: 52px;
  height: 52px;
  margin: 0 auto 12px;
  border-radius: 50%;
  background: #eef1f5;
  color: #44505c;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.seminar-audience-label {
  color: #333;
  font-size: 13px;
}

/* ---------- Process ---------- */
.seminar-process-section {
  background-color: rgb(15, 36, 27);
  padding: 70px 0;
}

.seminar-heading-bar-gold {
  background: #f5a623;
  margin-top: 0;
}

.seminar-process-desc {
  color: rgba(255, 255, 255, 0.7);
  max-width: 500px;
  margin-top: 16px;
  margin-bottom: 0;
}

.seminar-process-flow {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  row-gap: 28px;
  column-gap: 10px;
}

.seminar-process-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  position: relative;
}

.seminar-process-item::after {
  content: '';
  position: absolute;
  top: 26px;
  left: calc(50% + 32px);
  right: calc(-50% + 32px);
  border-top: 2px dashed rgba(255, 255, 255, 0.3);
}

.seminar-process-item:nth-child(4n)::after,
.seminar-process-item:last-child::after {
  content: none;
}

.seminar-process-icon {
  width: 54px;
  height: 54px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  border: 1.5px solid rgba(255, 255, 255, 0.3);
  color: #f5a623;
  font-size: 19px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.seminar-process-item p {
  font-size: 13px;
  text-align: center;
}

/* ---------- Commitment + Contact ---------- */
.seminar-commitment-card {
  border: 1.5px solid #e9e9e9;
  border-radius: 16px;
  padding: 32px;
  background: #fafbfb;
}

.seminar-commitment-divider {
  border: none;
  border-top: 1px solid #e3e3e3;
  margin: 28px 0;
}

.seminar-heading-dash-gold {
  background: #f5a623;
}

.seminar-doctor-card {
  background: #e9f5ef;
  border-radius: 14px;
  padding: 22px 26px;
  text-align: center;
}

.seminar-doctor-brand {
  font-weight: 800;
  color: #1a1a1a;
  font-size: 18px;
}

.seminar-doctor-bar {
  margin: 10px auto;
}

.seminar-doctor-tagline {
  font-family: 'Caveat', cursive;
  font-weight: 700;
  font-size: 26px;
  color: #1a1a1a;
}

.seminar-indonesia-tagline {
  font-weight: 700;
  color: #1a1a1a;
  font-size: 15px;
  line-height: 1.5;
}

.seminar-indonesia-subtagline {
  font-style: italic;
  color: #6c757d;
  font-size: 13px;
  line-height: 1.5;
  margin-top: 8px;
}

.seminar-contact-col-right {
  display: flex;
  align-items: center;
  gap: 14px;
}

@media (min-width: 992px) {
  .seminar-contact-col-right {
    border-left: 1px solid #e3e3e3;
    padding-left: 32px;
  }
}

.seminar-contact-icon {
  color: #008c45;
  font-size: 26px;
  flex: 0 0 auto;
}

@media (max-width: 991px) {
  .seminar-hero {
    padding-top: 170px;
  }
}
</style>
